import { TranslateModule } from '@ngx-translate/core';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { LogService } from '@services/util/log.service';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ParkingRequestFormComponent } from '../parking-request-form/parking-request-form.component';

/**
 * Container component for parking request creation/editing
 * Handles routing and parameter extraction for the form
 */
@Component({
  selector: 'app-parking-request-container',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, CardModule, ParkingRequestFormComponent],
  template: `
    <div class="card">
      <div class="card-header mb-4">
        <div class="flex items-center justify-between">
          <h1 class="text-2xl font-bold">
            {{ (isEditMode ? 'parking.form.editTitle' : 'parking.form.createTitle') | translate }}
          </h1>
          <button
            pButton
            icon="pi pi-arrow-left"
            [label]="'common.back' | translate"
            class="p-button-outlined"
            (click)="navigateBack()"
          ></button>
        </div>
      </div>

      <app-parking-request-form
        [initialBookingId]="bookingId"
        [initialParkingPlaceId]="parkingPlaceId"
        [initialStartDate]="startDate"
        [editMode]="isEditMode"
        [parkingRequestId]="parkingRequestId"
        (saved)="onSaved($event)"
        (cancelled)="navigateBack()"
        (loadError)="onLoadError($event)"
      >
      </app-parking-request-form>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParkingRequestContainerComponent implements OnInit {
  // Services
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private log = inject(LogService);

  // Form parameters
  bookingId: string | null = null;
  parkingPlaceId: string | null = null;
  startDate: Date | null = null;
  parkingRequestId: string | null = null;
  isEditMode = false;

  ngOnInit(): void {
    // DEBUG: Log container initialization
    this.log.debug('ParkingRequestContainerComponent ngOnInit called');
    this.extractRouteParameters();
  }

  /**
   * Extract parameters from route to initialize the form
   */
  private extractRouteParameters(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        // Edit mode
        this.isEditMode = true;
        this.parkingRequestId = id;
      }
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      // Get booking ID if provided
      const bookingId = queryParams.get('bookingId');
      if (bookingId) {
        this.bookingId = `/api/bookings/${bookingId}`;
      }

      // Get parking place ID if provided
      const parkingPlaceId = queryParams.get('parkingPlaceId');
      if (parkingPlaceId) {
        this.parkingPlaceId = `/api/flats/${parkingPlaceId}`;
      }

      // Get start date if provided
      const startDateParam = queryParams.get('startDate');
      if (startDateParam) {
        try {
          this.startDate = new Date(startDateParam);

          // Check if date is valid
          if (isNaN(this.startDate.getTime())) {
            this.startDate = null;
            this.log.warn('Invalid start date provided', startDateParam);
          }
        } catch (error) {
          this.startDate = null;
          this.log.error('Error parsing start date', error);
        }
      }
    });
  }

  /**
   * Navigate back to the parking overview or requests page
   */
  navigateBack(): void {
    this.router.navigate(['/parking']);
  }

  /**
   * Handle load error from form component
   * @param error The error that occurred during loading
   */
  onLoadError(error: any): void {
    // Check if this is a 404 error for parking request not found
    if (error?.isParkingRequest404) {
      this.log.info('Parking request not found, navigating to 404 page', { parkingRequestId: this.parkingRequestId });
      this.router.navigate(['/notfound']);
    }
    // Other errors are already handled by the form component with toast messages
  }

  /**
   * Handle saved event from form
   * @param parkingRequest The saved parking request
   */
  onSaved(parkingRequest: IParkingRequestModel): void {
    this.log.info('Parking request saved', parkingRequest);

    if (this.isEditMode) {
      // For edit mode, stay on the edit page (already showing the saved data)
      this.log.info('Parking request updated successfully');
    } else {
      // For creation mode, navigate to the edit route with the new ID
      if (parkingRequest.id) {
        this.log.info('Navigating to edit mode for newly created parking request', parkingRequest.id);
        this.router.navigate(['/parking/request', parkingRequest.id]);
      } else {
        // Fallback: navigate back if no ID is available
        this.log.warn('No ID available for newly created parking request, navigating back');
        this.navigateBack();
      }
    }
  }
}
